import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { t } from '@lingui/macro';
import styled, { keyframes, css } from 'styled-components';
import toColorString from 'polished/lib/color/toColorString';
import color from '@decisiv/design-tokens/lib/color';
import rem from 'polished/lib/helpers/rem';
import spacing from '@decisiv/design-tokens/lib/spacing';
import { Flex, P } from '@decisiv/ui-components';

import { elapsedSince } from '../../utils/dates';

/* ── Animations ── */
const pulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 color-mix(in srgb, var(--if-info) 40%, transparent); }
  50% { box-shadow: 0 0 0 6px transparent; }
`;

const checkmarkDraw = keyframes`
  to { stroke-dashoffset: 0; }
`;

/* ── Terminal status buckets ── */
const SUCCESS_STATUSES = ['SUCCEEDED', 'NOT_FOUND'];
const BLOCKED_STATUSES = [
  'BLOCKED_BY_CONDITION_RULE',
  'BLOCKED_BY_CIRCUIT_BREAKER',
  'RE_ENQUEUED_BY_CIRCUIT_BREAKER',
];

// Classify any terminal status into a step-status the wizard knows how to render.
function terminalBucket(status) {
  if (SUCCESS_STATUSES.includes(status)) return 'SUCCEEDED';
  if (status === 'COMPLETED_WITH_ERRORS') return 'COMPLETED_WITH_ERRORS';
  if (BLOCKED_STATUSES.includes(status)) return 'BLOCKED';
  return 'FAILED';
}

/* ── Status colors ── */
const STATUS_COLORS = {
  RUNNING: 'var(--if-info)',
  SUCCEEDED: toColorString(color.status.success.medium),
  COMPLETED_WITH_ERRORS: toColorString(color.status.warning.medium),
  BLOCKED: toColorString(color.status.warning.medium),
  FAILED: toColorString(color.status.danger.medium),
  pending: toColorString(color.base.halfMoon),
};

/* ── Styled components ── */
const WizardContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${rem(spacing.base)} 0 ${rem(spacing.base * 2)};
`;

const WizardHeader = styled(Flex)`
  align-items: center;
  justify-content: center;
  gap: ${rem(spacing.base)};
  margin-bottom: ${rem(spacing.base * 1.5)};
`;

const ElapsedBadge = styled.span`
  font-size: 13px;
  font-weight: 600;
  font-family: monospace;
  color: ${({ statusColor }) => statusColor || 'var(--if-info)'};
`;

const StepTrack = styled.div`
  display: flex;
  align-items: flex-start;
  padding: 0 ${rem(spacing.base * 2)};
`;

const StepItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  flex: 1;
  min-width: 72px;
`;

const StepConnector = styled.div`
  position: absolute;
  top: 12px;
  left: calc(50% + 14px);
  right: calc(-50% + 14px);
  height: 2px;
  background: ${({ stepStatus }) => {
    if (stepStatus === 'SUCCEEDED')
      return toColorString(color.status.success.medium);
    if (stepStatus === 'COMPLETED_WITH_ERRORS')
      return toColorString(color.status.warning.medium);
    if (stepStatus === 'BLOCKED')
      return toColorString(color.status.warning.medium);
    if (stepStatus === 'FAILED')
      return toColorString(color.status.danger.medium);
    if (stepStatus === 'RUNNING') return 'var(--if-info)';
    return toColorString(color.opacity.charcoal15);
  }};
  transition: background 0.3s ease;
`;

const StepIndicator = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 10px;
  font-weight: 700;
  color: var(--if-on-accent);
  background: ${({ stepStatus }) =>
    STATUS_COLORS[stepStatus] || STATUS_COLORS.pending};
  border: 2px solid
    ${({ stepStatus }) => STATUS_COLORS[stepStatus] || STATUS_COLORS.pending};
  transition: all 0.3s ease;

  ${({ stepStatus }) =>
    stepStatus === 'RUNNING' &&
    css`
      animation: ${pulse} 1.5s ease-in-out infinite;
    `}

  ${({ stepStatus }) =>
    stepStatus === 'pending' &&
    css`
      background: transparent;
      color: ${toColorString(color.base.alaskanHusky)};
    `}
`;

const StepLabel = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: ${toColorString(color.base.charcoal)};
  text-align: center;
  margin-top: 6px;
`;

/* ── Checkmark SVG ── */
const AnimatedPath = styled.path`
  stroke-dasharray: 14;
  stroke-dashoffset: 14;
  animation: ${checkmarkDraw} 0.3s ease forwards;
`;

function Checkmark() {
  return (
    <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
      <AnimatedPath
        d="M2.5 7.5L5.5 10.5L11.5 4"
        stroke="var(--if-on-accent)"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/* ── Cross SVG ── */
function CrossMark() {
  return (
    <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
      <path
        d="M2 2L10 10M10 2L2 10"
        stroke="var(--if-on-accent)"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

/* ── Exclamation SVG ── */
function BlockMark() {
  return (
    <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
      <path
        d="M6 2V7"
        stroke="var(--if-on-accent)"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <circle cx="6" cy="10" r="1" fill="var(--if-on-accent)" />
    </svg>
  );
}

function getIndicatorContent(status) {
  if (status === 'SUCCEEDED') return <Checkmark />;
  if (status === 'COMPLETED_WITH_ERRORS') return <BlockMark />;
  if (status === 'BLOCKED') return <BlockMark />;
  if (status === 'FAILED') return <CrossMark />;
  if (status === 'RUNNING') return '...';
  return '';
}

/* ── Main component ── */
export default function ExecutionWizard({
  executionStatus = null,
  integrationName = '',
}) {
  const [elapsed, setElapsed] = useState('');

  const overallStatus = executionStatus?.status;
  const isRunning = overallStatus === 'RUNNING';
  // Any terminal status (anything other than RUNNING / not-yet-started) is "done".
  const isDone = Boolean(overallStatus) && !isRunning;

  // Elapsed timer
  useEffect(() => {
    if (!executionStatus?.startDate || isDone) return undefined;

    const tick = () => {
      setElapsed(elapsedSince(executionStatus.startDate));
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [executionStatus?.startDate, isDone]);

  // Derive step statuses
  const started = Boolean(executionStatus);
  const startStatus = started ? 'SUCCEEDED' : 'pending';

  // Collapse the terminal status into one of the rendered buckets.
  const bucket = isDone ? terminalBucket(overallStatus) : null;

  let processingStatus = 'pending';
  if (isRunning) processingStatus = 'RUNNING';
  else if (isDone) processingStatus = bucket;

  const endStatus = isDone ? bucket : 'pending';

  const endLabel = (() => {
    if (overallStatus === 'SUCCEEDED') return t`Complete`;
    if (overallStatus === 'NOT_FOUND') return t`Complete – No Results`;
    if (BLOCKED_STATUSES.includes(overallStatus)) return t`Blocked`;
    if (isDone) return t`Failed`;
    return t`Waiting...`;
  })();

  const steps = [
    { key: 'start', label: t`Start`, status: startStatus },
    { key: 'processing', label: t`Processing`, status: processingStatus },
    { key: 'end', label: endLabel, status: endStatus },
  ];

  return (
    <WizardContainer>
      <WizardHeader>
        <P weight="medium" size="small">
          {integrationName}
        </P>
        {elapsed && (
          <ElapsedBadge
            statusColor={isDone ? STATUS_COLORS[bucket] : STATUS_COLORS.RUNNING}
          >
            {elapsed}
          </ElapsedBadge>
        )}
      </WizardHeader>

      <StepTrack>
        {steps.map((step, idx) => {
          const isLast = idx === steps.length - 1;

          return (
            <StepItem key={step.key}>
              {!isLast && <StepConnector stepStatus={step.status} />}
              <StepIndicator stepStatus={step.status}>
                {getIndicatorContent(step.status)}
              </StepIndicator>
              <StepLabel>{step.label}</StepLabel>
            </StepItem>
          );
        })}
      </StepTrack>
    </WizardContainer>
  );
}

ExecutionWizard.propTypes = {
  executionStatus: PropTypes.shape({
    status: PropTypes.string,
    startDate: PropTypes.string,
  }),
  integrationName: PropTypes.string,
};
